import { useEffect, useState } from "react";

import moment from "moment";
import { Button, DatePicker, Drawer, Form, Input, Select, Upload } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import type { UploadProps, UploadFile } from "antd";

import {
  ICnrcForm,
  ICreationDetails,
  useAdministrationPlaces,
  useEditCnrc
} from "../queries";
import { FieldData } from "rc-field-form/es/interface";
import { useAuth } from "providers/auth";

import styles from "./styles.module.scss";

interface Props {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  data?: ICreationDetails["cnrc"];
}

const CnrcDrawer: React.FC<Props> = ({ isOpen, onClose, data, id }) => {
  const [cnrcForm] = Form.useForm();
  const { accessToken } = useAuth();
  const [fileList, setFileList] = useState<UploadFile[] | undefined>();
  const { data: places } = useAdministrationPlaces({
    enabled: !!id
  });

  useEffect(() => {
    setFileList(
      data
        ? [
            {
              uid: "2",
              status: "success",
              name: data?.cnrc.split("/").slice(-1)[0],
              url: `${data?.cnrc}?t=${btoa(accessToken!)}`
            }
          ]
        : undefined
    );
  }, [accessToken, data]);

  const { mutate, isLoading } = useEditCnrc(id, {
    meta: { message: "Cnrc a bien etait edité" },
    onSuccess() {
      closeDrawer();
    },
    onError(error: any) {
      let formErrors: FieldData[] = [];
      for (let key in error.response.data.errors) {
        formErrors.push({
          name: key,
          errors: [error.response.data.errors[key][0].message]
        });
      }
      cnrcForm.setFields(formErrors);
    }
  });

  const onFinish = (values: ICnrcForm) => {
    const formData = new FormData();
    formData.append(
      "delivered_date",
      values.delivered_date.format("YYYY-MM-DD")
    );
    formData.append("delivered_place_id", values.delivered_place_id);
    formData.append("cnrc_number", values.cnrc_number);
    formData.append("cnrc", values.cnrc?.file?.originFileObj || "");
    mutate(formData);
  };
  const handleUploadChange: UploadProps["onChange"] = (info) => {
    setFileList([...info.fileList]);
  };

  const closeDrawer = () => {
    onClose();
    cnrcForm.resetFields();
  };

  return (
    <Drawer
      visible={isOpen}
      onClose={closeDrawer}
      title={"Completer reservation"}
    >
      <Form
        layout="vertical"
        form={cnrcForm}
        className={styles.form}
        onFinish={onFinish}
        initialValues={
          data
            ? {
                delivered_date: data?.delivered_date
                  ? moment(data?.delivered_date)
                  : null,
                delivered_place_id: data?.delivered_place.id,
                cnrc_number: data?.cnrc_number
              }
            : undefined
        }
      >
        <Form.Item
          rules={[
            { required: true, message: "Veuillez selectionner une date !" }
          ]}
          label="Date"
          name="delivered_date"
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="delivered_place_id"
          rules={[{ required: true, message: "Veuillez saisir un lieu !" }]}
          label="Lieu"
        >
          <Select placeholder="Veuillez sélectionner une adresse">
            {places?.map((place) => {
              if (place.type === "cnrc") {
                return (
                  <Select.Option key={place.id} value={place.id}>
                    {place.address}
                  </Select.Option>
                );
              } else {
                return null;
              }
            })}
          </Select>
        </Form.Item>
        <Form.Item
          rules={[
            {
              required: true,
              message: "Veuillez saisir le numéro de registre !"
            }
          ]}
          label="No registre"
          name="cnrc_number"
        >
          <Input />
        </Form.Item>
        <Form.Item
          rules={
            !data?.cnrc
              ? [
                  {
                    required: true,
                    message: "Veuillez saisir le fichier de registre !"
                  }
                ]
              : []
          }
          name="cnrc"
          label="Fichier registre"
        >
          <Upload.Dragger
            maxCount={1}
            customRequest={(options) =>
              setTimeout(() => options.onSuccess?.("ok"), 0)
            }
            onChange={handleUploadChange}
            fileList={fileList}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-hint">
              Cliquez ou faites glisser le fichier dans cette zone pour le
              télécharger
            </p>
          </Upload.Dragger>
        </Form.Item>
        <Form.Item>
          <Button loading={isLoading} type="primary" htmlType="submit">
            Sauvgarder
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default CnrcDrawer;
